export type KnnRowSourceType = 'specification' | 'physical' | 'unknown';

export const UNITLOAD_SIMILARITY_ALIASES = [
  'unitload_distance',
  'unitload_similarity',
  'Unitload Similarity %',
  'pallet_distance',
  'pallet_similarity',
  'Pallet Similarity %',
];

export const OVERALL_DECISION_ALIASES = [
  'Overall Decision',
  "('Overall Decision')",
  'OverallDecision',
  'Status',
  'DCS Status',
  'Test Status',
];

export function normalizeKnnColumnName(columnName: unknown): string {
  return String(columnName ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function findKnnColumnIndex(columns: unknown[] = [], aliases: string[]): number {
  const normalizedAliases = aliases.map(normalizeKnnColumnName);
  return columns.findIndex((columnName) => normalizedAliases.includes(normalizeKnnColumnName(columnName)));
}

export function getKnnValue(row: unknown[] = [], columns: unknown[] = [], aliases: string[]): unknown {
  const index = findKnnColumnIndex(columns, aliases);
  return index >= 0 ? row[index] : '';
}

export function formatKnnMeasurement(value: unknown, unit: string): string {
  if (value === undefined || value === null || value === '') return 'N/A';
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${numericValue.toFixed(2)}${unit}` : String(value);
}

export function formatKnnPercent(value: unknown): number {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? parseFloat(numericValue.toFixed(2)) : 0;
}

export function getKnnRowSourceType(row: unknown[] = [], columns: unknown[] = [], optionNo: unknown): KnnRowSourceType {
  if (optionNo !== undefined && row.length === 0 && columns.length === 0) {
    return 'unknown';
  }

  const optionIdentifier = String(getKnnValue(row, columns, [
    'Option Number',
    'Option No',
    'Option ID',
    'SPEC NUMBER',
    'Spec Number',
  ]) || optionNo || '').trim().toUpperCase();
  const explicitSource = String(getKnnValue(row, columns, [
    'Data Source',
    'Record Type',
    'Record Source',
    'Source',
    'Source Type',
    'Status',
  ]) || '').toLowerCase();
  const variableName = String(getKnnValue(row, columns, [
    'Variable Name',
    'Pallet Variant',
    'Variable',
    'Test Variable',
  ]) || '').toLowerCase();
  const hasShipTestValues = [
    'PreShip_Lean',
    'PostShip_Lean',
    'PostShip_Walk',
    'PostStack_Lean',
    'Post Stack Compression_Rebound',
  ].some((columnName) => {
    const value = getKnnValue(row, columns, [columnName]);
    return value !== undefined && value !== null && value !== '';
  });

  if (explicitSource === 'pv' || explicitSource.includes('spec') || optionIdentifier.startsWith('P')) {
    return 'specification';
  }
  if (
    explicitSource.includes('dcs') ||
    explicitSource.includes('bwdg') ||
    explicitSource.includes('physical') ||
    explicitSource === 'pass' ||
    explicitSource === 'fail' ||
    variableName.includes('physical') ||
    hasShipTestValues
  ) {
    return 'physical';
  }

  return 'unknown';
}
